import GeniePage from "../../../components/special/genie-page/GeniePage";
import React, { useState, useRef } from "react";
import GenieText from "../../../components/core/genie-text/GenieText";
import Box from "@mui/material/Box";
import { Cancel, CheckCircle } from "@mui/icons-material";
import GenieSpinner from "../../../components/core/genie-spinner/GenieSpinner";
import InviteDialog from "../onboarding/dialogs/Invite";
import { useParams } from "react-router-dom";
import { Chip, Snackbar, Alert } from "@mui/material";
import useOnboardingStatus from "../../../queries/routes/useOnboardingStatus";
import AddressDialog from "../onboarding/dialogs/Address";
import OrderImport from "../../../components/special/order-import/OrderImport";

type Header =
  | "Customize Your Store"
  | "Add Your Products"
  | "Add Your Wholesale Terms"
  | "Invite Your Customers"
  | "Add Shipping Details"
  | "Set Up Payments"
  | "Upload Past Orders";

const WELCOME_CONFIG: {
  header: Header;
  link?: string;
  text: string;
  dialog?: string;
  beta: boolean;
}[] = [
  {
    header: "Customize Your Store",
    link: "onboarding",
    text: "Add your logo, cover photo, and a short description. This will be seen by your buyers.",
    beta: false,
  },
  {
    header: "Set Up Payments",
    link: "organization/onboarding/stripe-connect",
    text: "Set up a Stripe account to accept payments.",
    beta: false,
  },
  {
    header: "Add Your Products",
    link: "products",
    text: "Upload your catalog for buyers to purchase online.",
    beta: false,
  },
  {
    header: "Add Your Wholesale Terms",
    link: "terms",
    text: "Set up your minimum order quantity.",
    beta: false,
  },
  {
    header: "Invite Your Customers",
    dialog: "invite",
    text: "Let your existing customers know that they can purchase online & start selling!",
    link: "welcome",
    beta: false,
  },
  {
    header: "Add Shipping Details",
    dialog: "address",
    text: "Add your address and package info so we can calculate shipping accurately.",
    link: "welcome",
    beta: false,
  },
  {
    header: "Upload Past Orders",
    link: "welcome",
    text: "Upload your past orders so we can set up reorder reminders.",
    beta: false,
  },
];

function MerchantChecklist() {
  let { org_id } = useParams();
  const { data: onboardingStatus } = useOnboardingStatus(org_id!);
  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  const [addressDialogOpen, setAddressDialogOpen] = useState<boolean>(false);
  const orderImportRef = useRef<{ triggerUpload: () => void }>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const evaluateStatus = (header: Header) => {
    if (!onboardingStatus) {
      return false;
    }
    switch (header) {
      case "Customize Your Store":
        return (
          onboardingStatus.has_description && onboardingStatus.has_splash_image
        );
      case "Set Up Payments":
        return onboardingStatus.has_stripe_connect;
      case "Add Your Products":
        return onboardingStatus.product_count > 0;
      case "Add Your Wholesale Terms":
        return onboardingStatus.has_wholesale_terms;
      case "Invite Your Customers":
        return onboardingStatus.has_customers;
      case "Add Shipping Details":
        return onboardingStatus.has_address;
      case "Upload Past Orders":
        return onboardingStatus.has_past_orders;
      default:
        return false;
    }
  };
  if (!onboardingStatus) {
    return <GenieSpinner />;
  }

  function setClosed() {
    setInviteDialogOpen(false);
    setAddressDialogOpen(false);
  }

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <GeniePage>
        <Box sx={{ display: 'none' }}>
          <OrderImport
            ref={orderImportRef}
            organizationId={org_id!}
            showSnackbar={false}
            onSuccess={(data) => {
              let message = `Successfully processed ${data.processed_orders} orders.`;

              if (data.existing_orders > 0) {
                message += ` ${data.existing_orders} orders were previously imported and skipped.`;
              }

              // if (data.errors.length > 0) {
              //   message += ` Errors: ${data.errors.length}`;
              // }

              console.log("Setting snackbar with message:", message, "errors:", data.errors);

              setSnackbar({
                open: true,
                message,
                severity: "success",
              });
            }}
            onError={(error) => {
              const message = error.message || "Failed to upload past orders";
              setSnackbar({
                open: true,
                message,
                severity: "error",
              });
            }}
          />
        </Box>
        <InviteDialog open={inviteDialogOpen} setClosed={setClosed} />
        <AddressDialog open={addressDialogOpen} setClosed={setClosed} />
        <Box mb={4}>
          <GenieText header fontWeight={"bold"}>
            Welcome! To set up your store, start here:
          </GenieText>
        </Box>
        {WELCOME_CONFIG.map((config, index) => (
          <Box key={index}>
            <Box mb={1}>
              <GenieText
                fontSize={"medium"}
                fontWeight={"bold"}
                // link={config.header === "Upload Past Orders" ? undefined : `/app/${org_id}/${config.link}`}
                link={`/app/${org_id}/${config.link}`}
                onClick={() => {
                  if (config.dialog === "invite") {
                    setInviteDialogOpen(true);
                  } else if (config.dialog === "address") {
                    setAddressDialogOpen(true);
                  } else if (config.header === "Upload Past Orders") {
                    orderImportRef.current?.triggerUpload();
                  }
                }}
              >
                <Box display={"flex"} alignItems={"center"}>
                  {evaluateStatus(config.header) ? (
                    <CheckCircle
                      width={24}
                      height={24}
                      sx={{ mr: 2 }}
                      color={"info"}
                    />
                  ) : (
                    <Cancel
                      width={24}
                      height={24}
                      sx={{ mr: 2 }}
                      color={"error"}
                    />
                  )}
                  {index + 1 + " - " + config.header}
                  {config.beta && (
                    <Chip
                      sx={{ ml: 1 }}
                      variant={"outlined"}
                      color={"warning"}
                      label="beta"
                      size="small"
                    />
                  )}
                </Box>
              </GenieText>
            </Box>
            <GenieText paragraph fontSize={"small"}>
              {config.text}
            </GenieText>
          </Box>
        ))}
      </GeniePage>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default MerchantChecklist;
